<template>
  <div
    ref="buttonWrapper"
    @mousedown="onMouseDown"
    @mouseup="onMouseUp"
    @mouseleave="onMouseUp"
    :class="wrapperClassList"
    :style="[buttonStyles, disabled || btnLoading ? {pointerEvents: 'none'} : {}]"
  >
    <a
      v-if="href"
      :href="href"
      :target="targetBlank ? '_blank' : ''"
      ref="button"
      :class="buttonClassList"
      :disabled="disabled || btnLoading"
      :style="[
        color === 'hollow' || size === 'sm' ? {color: $helpers.getCorrectColor(themeColorHEX)} : {},
        active ? {border: `2px solid ${$helpers.getCorrectColor(themeColorHEX)} !important`} : {},
        disabled ? {opacity: '.6'} : ''
      ]"
    >
      <slot v-if="!btnLoading" name="left-side"></slot>
      <div v-else-if="btnLoading" v-html="loadingCircleSVG"></div>
      <div class="w-full text-left" v-if="leftSided">
        <slot></slot>
      </div>
      <slot v-else></slot>
      <slot name="right-side"></slot>
    </a>
    <button
      v-else
      ref="button"
      :class="buttonClassList"
      :disabled="disabled || btnLoading"
      :style="[
        color === 'hollow' || size === 'sm' ? {color: $helpers.getCorrectColor(themeColorHEX)} : {},
        active ? {border: `2px solid ${$helpers.getCorrectColor(themeColorHEX)} !important`} : {},
        disabled ? {opacity: '.6'} : ''
      ]"
    >
      <slot v-if="!btnLoading" name="left-side"></slot>
      <div v-else-if="btnLoading" v-html="loadingCircleSVG"></div>
      <div class="w-full text-left" v-if="leftSided">
        <slot></slot>
      </div>
      <slot v-else></slot>
      <slot name="right-side"></slot>
    </button>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  export default {
    name: 't-button',
    data() {
      return {
        btnLoading: false,
        mouseActing: false
      }
    },
    props: {
      border: {
        type: Boolean,
        default: false
      },
      active: {
        type: Boolean,
        default: false
      },
      disabled: {
        type: Boolean,
        default: false
      },
      size: {
        type: String,
        validator: function (value) {
          return ['lg', 'sm'].indexOf(value) !== -1
        },
        default: 'lg'
      },
      customWidth: {
        type: Boolean,
        default: false
      },
      leftSided: {
        type: Boolean,
        default: false
      },
      rounded: {
        type: Boolean,
        default: true
      },
      buttonClass: {
        type: String,
        default: ''
      },
      color: {
        type: String,
        validator: function (value) {
          return ['branded', 'default', 'hollow'].indexOf(value) !== -1
        },
        default: 'default'
      },
      href: {
        type: String
      },
      targetBlank: {
        type: Boolean,
        default: true
      }
    },
    computed: {
      ...mapGetters(['themeColorHEX', 'themeColorRGBA', 'theme']),
      wrapperClassList() {
        const wrapperBaseClass = ['relative', 'before:block', 'before:absolute', 'before:inset-0', 'before:z-10']

        if (this.color === 'default') {
          if (this.theme === 'light') wrapperBaseClass.push('before:bg-white')
          if (this.theme === 'dark') wrapperBaseClass.push('before:bg-silver')
        }
        if (!this.customWidth) {
          wrapperBaseClass.push('w-full')
        }
        if (this.color === 'branded') {
          wrapperBaseClass.push(`before:bg-[${this.$helpers.getCorrectColor(this.themeColorHEX)}]`)
        }
        if (this.color === 'hollow') {
          wrapperBaseClass.push('before:bg-transparent')
        }

        if (this.size === 'lg') {
          if (this.rounded) wrapperBaseClass.push('rounded-lg', 'before:rounded-lg')
          wrapperBaseClass.push('h-12')
        }
        if (this.size === 'sm') {
          if (this.rounded) wrapperBaseClass.push('rounded-lg', 'before:rounded-lg')
          wrapperBaseClass.push('h-10')
        }

        return wrapperBaseClass
      },
      buttonClassList() {
        const buttonBaseClass = [
          'w-full',
          'flex',
          'justify-center',
          'items-center',
          'gap-2',
          'transition-all',
          'duration-200',
          'relative',
          'z-20',
          'cursor-pointer',
          'disabled:pointer-events-none',
          'px-3'
        ]
        const buttonColorClass = []

        if (this.color === 'default') {
          buttonColorClass.push('bg-transparent', 'active:bg-black/10', 'disabled:text-gray')
          if (this.size === 'lg') {
            buttonColorClass.push('hover:bg-black/5', 'text-black')
          }
        }
        if (this.color === 'branded') {
          if (this.theme === 'light') {
            buttonColorClass.push('bg-transparent', 'active:bg-white/5', 'disabled:text-white/50 text-white')
          } else {
            buttonColorClass.push(
              'bg-transparent',
              'active:bg-white/5',
              this.$helpers.getColorBrightness(this.themeColorHEX) >= 175 || this.$helpers.getColorBrightness(this.themeColorHEX) <= 65
                ? 'disabled:text-white/50 text-white'
                : 'disabled:text-black/50 text-black'
            )
          }
          if (this.size === 'lg') {
            buttonColorClass.push('hover:bg-white/10')
          }
        }
        if (this.color === 'hollow') {
          buttonColorClass.push('bg-transparent', 'active:bg-black/10', 'disabled:text-gray')
          if (this.size === 'lg') {
            buttonColorClass.push('hover:bg-black/5')
          }
        }

        if (this.buttonClass) {
          buttonBaseClass.push(this.buttonClass)
        }

        if (this.size === 'lg') {
          if (this.rounded) buttonBaseClass.push('rounded-lg')
          buttonBaseClass.push('h-12')
        }
        if (this.size === 'sm') {
          if (this.rounded) buttonBaseClass.push('rounded-lg')
          buttonBaseClass.push('h-10', 'disabled:text-gray')
        }

        if (this.border) {
          buttonBaseClass.push('border-2', 'border-solid', 'border-platinum', `active:bg-white`)
        } else {
          buttonBaseClass.push('border-2', 'border-solid', 'border-transparent')
        }

        return [...buttonBaseClass, ...buttonColorClass]
      },
      buttonStyles() {
        const styles = {}
        if (this.color === 'branded' && this.size === 'lg') {
          if (this.$helpers.isColorBright(this.themeColorHEX)) {
            styles.boxShadow = `0px 5px 10px rgba(${this.$helpers.hexToRGB(this.$helpers.getCorrectColor(this.themeColorHEX)).r}, ${
              this.$helpers.hexToRGB(this.$helpers.getCorrectColor(this.themeColorHEX)).g
            }, ${this.$helpers.hexToRGB(this.$helpers.getCorrectColor(this.themeColorHEX)).b}, 0.4)`
          } else {
            styles.boxShadow = `0px 5px 10px rgba(${this.themeColorRGBA.r}, ${this.themeColorRGBA.g}, ${this.themeColorRGBA.b}, 0.4)`
          }
          styles.background = this.$helpers.getCorrectColor(this.themeColorHEX)
        }
        if (this.color === 'default' && this.size === 'lg') {
          styles.boxShadow = '0px 5px 10px rgba(31, 31, 31, 0.12)'
        }

        return styles
      },
      loadingCircleSVG() {
        let fill = this.$helpers.getCorrectColor(this.themeColorHEX)
        if (this.color === 'branded') {
          if (this.theme === 'light') {
            fill = '#fff'
          } else {
            if (
              this.$helpers.getColorBrightness(this.themeColorHEX) >= 175 ||
              this.$helpers.getColorBrightness(this.themeColorHEX) <= 65
            ) {
              fill = '#1e1e1e'
            } else {
              fill = '#fff'
            }
          }
        }
        return `<svg
                  style="width: 24px; height: 24px"
                  id="loader"
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                  xmlns:xlink="http://www.w3.org/1999/xlink"
                  x="0px"
                  y="0px"
                  viewBox="0 0 100 100"
                  enable-background="new 0 0 0 0"
                  xml:space="preserve"
                >
                  <path fill="${fill}" d="M73,50c0-12.7-10.3-23-23-23S27,37.3,27,50 M30.9,50c0-10.5,8.5-19.1,19.1-19.1S69.1,39.5,69.1,50">
                    <animateTransform
                      attributeName="transform"
                      attributeType="XML"
                      type="rotate"
                      dur="1s"
                      from="0 50 50"
                      to="360 50 50"
                      repeatCount="indefinite"
                    />
                  </path>
                </svg>
        `
      }
    },
    methods: {
      toggleLoad() {
        this.btnLoading = !this.btnLoading
      },
      onMouseDown() {
        if (this.active) return
        this.mouseActing = true
        const styles = []
        if (this.border)
          styles.push(
            `border-color: ${this.$helpers.getCorrectColor(this.themeColorHEX)} !important; color: ${this.$helpers.getCorrectColor(
              this.themeColorHEX
            )} !important;`
          )
        if (this.color === 'hollow' || (this.size === 'sm' && !this.border))
          styles.push(`color: ${this.$helpers.getCorrectColor(this.themeColorHEX)} !important;`)
        this.$refs.button.style.cssText = styles.join(' ')
      },
      onMouseUp() {
        if (this.active) return
        if (!this.mouseActing) return
        this.$refs.button.style.removeProperty('border-color')
        this.mouseActing = false
      }
    }
  }
</script>
